'use client';
import { useState, useMemo } from 'react';
import {
  filterPricesByRange,
  RANGES,
  type PricePoint, type PriceRange,
} from '@/lib/stock-detail-helpers';

// ─── Price Chart ─────────────────────────────────────────────────────────────

export function PriceChart({ allPrices }: { allPrices: PricePoint[] }) {
  const [range, setRange] = useState<PriceRange>('1Y');
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const prices = useMemo(() => filterPricesByRange(allPrices, range), [allPrices, range]);

  // Hooks must run unconditionally — compute MAs before any early return below.
  const closes = useMemo(() => prices.map(p => p.close), [prices]);
  const ma50 = useMemo(() => {
    if (prices.length < 50) return null;
    return prices.map((_, i) => {
      if (i < 49) return null;
      const sum = closes.slice(i - 49, i + 1).reduce((a, b) => a + b, 0);
      return sum / 50;
    });
  }, [prices, closes]);
  const ma200 = useMemo(() => {
    if (prices.length < 200) return null;
    return prices.map((_, i) => {
      if (i < 199) return null;
      const sum = closes.slice(i - 199, i + 1).reduce((a, b) => a + b, 0);
      return sum / 200;
    });
  }, [prices, closes]);

  if (prices.length < 2) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] font-semibold uppercase tracking-widest text-[#e2e8f0]">Price</span>
          <div className="flex gap-0.5">
            {RANGES.map(r => (
              <button key={r} onClick={() => setRange(r)}
                className={`px-1.5 py-0.5 text-[9px] font-semibold rounded transition-colors duration-100 cursor-pointer
                           ${r === range ? 'bg-blue-500/20 text-blue-400' : 'text-white hover:text-[#e2e8f0]'}`}>
                {r}
              </button>
            ))}
          </div>
        </div>
        <div className="text-white text-[10px] py-2">Not enough data for this range</div>
      </div>
    );
  }

  const W = 500;
  const H = 140;
  const PAD = { top: 8, right: 8, bottom: 20, left: 42 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  // Include MAs in min/max calculation
  const allValues = [...closes];
  if (ma50) allValues.push(...ma50.filter((v): v is number => v !== null));
  if (ma200) allValues.push(...ma200.filter((v): v is number => v !== null));
  
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const spreadRange = max - min || 1;

  const toX = (i: number) => PAD.left + (i / (prices.length - 1)) * chartW;
  const toY = (v: number) => PAD.top + chartH - ((v - min) / spreadRange) * chartH;

  const linePath = prices.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(p.close).toFixed(1)}`).join(' ');
  const areaPath = linePath + ` L${toX(prices.length - 1).toFixed(1)},${(PAD.top + chartH).toFixed(1)} L${PAD.left},${(PAD.top + chartH).toFixed(1)} Z`;

  const firstClose = prices[0].close;
  const lastClose = prices[prices.length - 1].close;
  const pctChange = ((lastClose - firstClose) / firstClose) * 100;
  const isUp = pctChange >= 0;
  const lineColor = isUp ? '#22c55e' : '#ef4444';
  const areaColor = isUp ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)';

  const step = spreadRange / 3;
  const yTicks = [min, min + step, min + step * 2, max];
  const monthLabels: { x: number; label: string }[] = [];
  let lastLabel = '';
  const useYear = range === '5Y' || range === 'Max';
  for (let i = 0; i < prices.length; i++) {
    const d = new Date(prices[i].date);
    const label = useYear
      ? (d.getMonth() === 0 ? d.getFullYear().toString() : '')
      : d.toLocaleDateString('en-US', { month: 'short' });
    if (label && label !== lastLabel) {
      lastLabel = label;
      monthLabels.push({ x: toX(i), label });
    }
  }
  const maxLabels = useYear ? 10 : 7;
  const labelStep = Math.max(1, Math.ceil(monthLabels.length / maxLabels));
  const sparseLabels = monthLabels.filter((_, i) => i % labelStep === 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <span className="text-[9px] font-semibold uppercase tracking-widest text-[#e2e8f0]">Price</span>
          <span className="text-[11px] font-bold tabular-nums" style={{ color: lineColor, fontFamily: 'var(--font-mono)' }}>
            ${lastClose.toFixed(2)} <span className="text-[10px] font-semibold">({isUp ? '+' : ''}{pctChange.toFixed(1)}%)</span>
          </span>
          {ma50 && (
            <span className="text-[8px] text-blue-400 flex items-center gap-1">
              <span className="inline-block w-3 h-0.5 bg-blue-400 opacity-70" style={{ borderTop: '1px dashed' }}></span>
              50d
            </span>
          )}
          {ma200 && (
            <span className="text-[8px] text-amber-400 flex items-center gap-1">
              <span className="inline-block w-3 h-0.5 bg-amber-400 opacity-70" style={{ borderTop: '1px dashed' }}></span>
              200d
            </span>
          )}
        </div>
        <div className="flex gap-0.5">
          {RANGES.map(r => (
            <button key={r} onClick={() => setRange(r)}
              className={`px-1.5 py-0.5 text-[9px] font-semibold rounded transition-colors duration-100 cursor-pointer
                         ${r === range ? 'bg-blue-500/20 text-blue-400' : 'text-white hover:text-[#e2e8f0]'}`}>
              {r}
            </button>
          ))}
        </div>
      </div>
      <svg width={W} height={H} className="w-full" viewBox={`0 0 ${W} ${H}`}
        onMouseMove={(e) => {
          const svg = e.currentTarget;
          const rect = svg.getBoundingClientRect();
          const scaleX = W / rect.width;
          const mouseX = (e.clientX - rect.left) * scaleX;
          const idx = Math.round(((mouseX - PAD.left) / chartW) * (prices.length - 1));
          setHoverIdx(idx >= 0 && idx < prices.length ? idx : null);
        }}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <path d={areaPath} fill={areaColor} />
        <path d={linePath} fill="none" stroke={lineColor} strokeWidth={1.5} />
        
        {/* 50-day MA */}
        {ma50 && (() => {
          const ma50Path = ma50.map((v, i) => 
            v !== null ? `${i === 0 || ma50[i-1] === null ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(v).toFixed(1)}` : ''
          ).join(' ');
          return <path d={ma50Path} fill="none" stroke="#3b82f6" strokeWidth={1} opacity={0.7} strokeDasharray="2,2" />;
        })()}
        
        {/* 200-day MA */}
        {ma200 && (() => {
          const ma200Path = ma200.map((v, i) => 
            v !== null ? `${i === 0 || ma200[i-1] === null ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(v).toFixed(1)}` : ''
          ).join(' ');
          return <path d={ma200Path} fill="none" stroke="#f59e0b" strokeWidth={1} opacity={0.7} strokeDasharray="4,2" />;
        })()}
        {yTicks.map((v, i) => (
          <text key={i} x={PAD.left - 4} y={toY(v) + 3} fill="#e2e8f0" fontSize={8} textAnchor="end"
            fontFamily="var(--font-mono), monospace">
            ${v.toFixed(0)}
          </text>
        ))}
        {sparseLabels.map((l, i) => (
          <text key={i} x={l.x} y={H - 4} fill="#e2e8f0" fontSize={8} textAnchor="middle"
            fontFamily="var(--font-mono), monospace">
            {l.label}
          </text>
        ))}
        <line x1={PAD.left} y1={PAD.top + chartH} x2={PAD.left + chartW} y2={PAD.top + chartH}
          stroke="rgba(255,255,255,0.06)" strokeWidth={0.5} />
        {/* Hover crosshair + tooltip */}
        {hoverIdx !== null && prices[hoverIdx] && (() => {
          const hp = prices[hoverIdx];
          const hx = toX(hoverIdx);
          const hy = toY(hp.close);
          const hPct = ((hp.close - firstClose) / firstClose) * 100;
          const hDate = new Date(hp.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
          return (
            <g>
              <line x1={hx} y1={PAD.top} x2={hx} y2={PAD.top + chartH} stroke="rgba(255,255,255,0.3)" strokeWidth={0.5} strokeDasharray="2,2" />
              <circle cx={hx} cy={hy} r={3} fill={lineColor} stroke="#0a0c10" strokeWidth={1.5} />
              <rect x={hx - 48} y={hy - 32} width={96} height={24} rx={4}
                fill="rgba(10,12,16,0.95)" stroke="rgba(255,255,255,0.15)" strokeWidth={0.5} />
              <text x={hx} y={hy - 20} fill="#e2e8f0" fontSize={9} textAnchor="middle"
                fontFamily="var(--font-mono), monospace" fontWeight={600}>
                ${hp.close.toFixed(2)}
              </text>
              <text x={hx} y={hy - 12} fill="#94a3b8" fontSize={7} textAnchor="middle"
                fontFamily="var(--font-mono), monospace">
                {hDate} ({hPct >= 0 ? '+' : ''}{hPct.toFixed(1)}%)
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}
